import { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, getDocs, where, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase/config';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { phoneQueryVariants } from '@/lib/accountDeletion/identity';
import {
  resolveTarget,
  readPhoneOwnership,
  confirmableUid,
  type TargetResolution,
  type PhoneOwnership,
} from '@/lib/accountDeletion/approvalTarget';
import { deletionStrings } from '@/lib/accountDeletion/strings';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, Trash2, CheckCircle2, XCircle, Clock, Phone, Mail, MessageSquare, AlertTriangle, ShieldAlert, User } from 'lucide-react';

interface DeletionRequest {
  id: string;
  phone: string;
  email: string | null;
  reason: string | null;
  userId?: string | null;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Date;
  processedAt: Date | null;
  processedBy: string | null;
  /** Server-written bookkeeping. Every field is world-writable — see the callable. */
  deletion?: Record<string, unknown>;
  phoneOwnershipProven?: boolean;
}

interface DeletionReport {
  uid?: string;
  retained?: string[];
  authAccount?: string;
  userDoc?: string;
  storage?: { objectsFound: number; objectsDeleted: number; failures: number };
}

/** The confirmation step: which request, which account, and has the admin ticked the box. */
interface ConfirmState {
  request: DeletionRequest;
  resolution: TargetResolution | null; // null while the lookup is in flight
  ownership: PhoneOwnership;
  checked: boolean;
}

interface DeletionRequestsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeletionRequestsDialog({ open, onOpenChange }: DeletionRequestsDialogProps) {
  const { language } = useLanguage();
  const { user } = useAuth();
  const lang = language === 'ar' ? 'ar' : 'en';
  const txt = deletionStrings[lang];
  const isRTL = lang === 'ar';
  const dateLocale = lang === 'ar' ? 'ar-SA-u-nu-latn-ca-gregory' : 'en-US';

  const [requests, setRequests] = useState<DeletionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [actionError, setActionError] = useState<{ title: string; message: string } | null>(null);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);

  useEffect(() => {
    if (!open) return;
    // A banner from a previous session would otherwise reappear detached from
    // any action the admin just took.
    setActionError(null);
    setConfirmState(null);

    const q = query(collection(db, 'accountDeletionRequests'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate?.() || new Date(),
        processedAt: d.data().processedAt?.toDate?.() || null,
      })) as DeletionRequest[];
      setRequests(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [open]);

  // Approval is a server-side cascade, not a status stamp. The callable refuses
  // (and deletes nothing) when the phone does not resolve to exactly one user,
  // so every failure below means the account is still intact — say so plainly
  // instead of swallowing it into console.error.
  // `intact: true` means the function refused and the account is untouched.
  // `intact: false` means data may already be gone — the headline must not
  // claim "nothing was deleted" in that case.
  const failureMessage = (error: unknown): { title: string; message: string } => {
    const msg = String((error as { message?: string })?.message || '');
    const code = String((error as { code?: string })?.code || '');
    const refused = (message: string) => ({ title: txt.failedTitle, message });
    const unknown = (message: string) => ({ title: txt.partialTitle, message });

    if (msg.includes('confirm-uid-required')) return refused(txt.errConfirmRequired);
    if (msg.includes('confirm-uid-mismatch')) return refused(txt.errConfirmMismatch);
    if (msg.includes('ambiguous-match')) return refused(txt.errAmbiguous);
    if (msg.includes('identity-conflict')) return refused(txt.errConflict);
    if (msg.includes('unverified-identity')) return refused(txt.errUnverified);
    if (msg.includes('no-match')) return refused(txt.errNoMatch);
    if (msg.includes('target-is-admin')) return refused(txt.errTargetAdmin);
    if (msg.includes('request-rejected')) return refused(txt.errRejected);
    if (code === 'functions/permission-denied' || code === 'functions/unauthenticated') {
      return refused(txt.errPermission);
    }
    // Everything below may have deleted something before failing.
    if (msg.includes('deletion-incomplete')) return unknown(txt.errIncomplete);
    if (code === 'functions/deadline-exceeded') return unknown(txt.errTimeout);
    return unknown(txt.errGeneric);
  };

  /**
   * Look the request's phone up against `users` and return the real account.
   *
   * This is what the admin actually confirms. The phone/email/reason printed on
   * the request card were typed into an unauthenticated public form; the name
   * and join date below come from the account that would be destroyed.
   */
  const lookUpTarget = async (req: DeletionRequest): Promise<TargetResolution> => {
    const variants = phoneQueryVariants(req.phone);
    if (variants.length === 0) return { ok: false, reason: 'no-match', candidateUids: [] };
    try {
      const found: Array<{ uid: string; data: Record<string, unknown> }> = [];
      for (const field of ['phone', 'phoneNumber'] as const) {
        const snap = await getDocs(query(collection(db, 'users'), where(field, 'in', variants)));
        snap.docs.forEach((d) => found.push({ uid: d.id, data: d.data() as Record<string, unknown> }));
      }
      return resolveTarget(found);
    } catch {
      return { ok: false, reason: 'lookup-failed', candidateUids: [] };
    }
  };

  const openConfirm = async (req: DeletionRequest) => {
    setActionError(null);
    setConfirmState({
      request: req,
      resolution: null,
      ownership: readPhoneOwnership(req as unknown as Record<string, unknown>),
      checked: false,
    });
    const resolution = await lookUpTarget(req);
    setConfirmState((prev) => (
      prev && prev.request.id === req.id ? { ...prev, resolution } : prev
    ));
  };

  const runApproval = async (req: DeletionRequest, confirmUid: string) => {
    setConfirmState(null);
    setProcessingId(req.id);
    try {
      const approveAccountDeletion = httpsCallable<
        { requestId: string; confirmUid: string },
        { ok: boolean; uid?: string; alreadyCompleted?: boolean; report?: DeletionReport }
      >(
        functions,
        'approveAccountDeletion',
        // The callable is provisioned for 540s. The SDK default is 70s, which
        // would abort a large-but-succeeding cascade and report it as a failure.
        { timeout: 540_000 },
      );

      // confirmUid names the ACCOUNT. The server refuses unless its own
      // resolution of the request produces exactly this uid, so a forged
      // request can no longer be actioned by approving it blind.
      const result = await approveAccountDeletion({ requestId: req.id, confirmUid });
      if (!result.data?.ok) {
        // Never let the UI claim success the function did not report.
        throw new Error('deletion-incomplete');
      }
      toast.success(result.data.alreadyCompleted ? txt.alreadyDeleted : txt.deleted);
      toast.info(txt.retainedNote);
      // The request document is updated server-side; onSnapshot picks it up.
    } catch (error) {
      console.error('Error approving deletion request:', error);
      const failure = failureMessage(error);
      setActionError(failure);
      toast.error(failure.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (req: DeletionRequest) => {
    if (!confirm(txt.confirmReject)) return;
    setActionError(null);
    setProcessingId(req.id);
    try {
      await updateDoc(doc(db, 'accountDeletionRequests', req.id), {
        status: 'rejected',
        processedAt: Timestamp.now(),
        processedBy: user?.email || user?.uid || 'admin',
      });
    } catch (error) {
      console.error('Error rejecting request:', error);
      setActionError({ title: txt.failedTitle, message: txt.errReject });
      toast.error(txt.errReject);
    } finally {
      setProcessingId(null);
    }
  };

  const filtered = filter === 'all' ? requests : requests.filter((r) => r.status === filter);
  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  const formatDate = (d: Date | null) => (d
    ? d.toLocaleDateString(dateLocale, { year: 'numeric', month: 'short', day: 'numeric' })
    : txt.joinedUnknown);

  const statusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            <Clock className="w-3 h-3" /> {txt.pending}
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle2 className="w-3 h-3" /> {txt.approved}
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <XCircle className="w-3 h-3" /> {txt.rejected}
          </span>
        );
    }
  };

  // -------------------------------------------------------------------------
  // The confirmation step. Everything the admin needs in order to notice that
  // the request is aimed at the wrong person is on this screen, read from the
  // account rather than from the request.
  // -------------------------------------------------------------------------
  const renderConfirm = () => {
    if (!confirmState) return null;
    const { request: req, resolution, ownership, checked } = confirmState;
    const uidToConfirm = resolution
      ? confirmableUid(resolution, req as unknown as Record<string, unknown>)
      : null;
    const account = resolution?.ok ? resolution.account : null;
    const resuming = !!uidToConfirm && !account;

    // Why there is no Approve button. An unexplained missing button is how an
    // admin ends up retrying the same destructive action.
    const blockedMessage = (() => {
      if (!resolution) return null;
      // Resolved, but the callable would refuse it anyway.
      if (resolution.ok) return resolution.account.isAdmin ? txt.resolveIsAdmin : null;
      if (uidToConfirm) return null; // resumable
      if (resolution.reason === 'ambiguous-match') return txt.resolveAmbiguous;
      if (resolution.reason === 'lookup-failed') return txt.resolveFailed;
      return txt.resolveNoMatch;
    })();

    const row = (label: string, value: string | null, ltr = false) => (
      <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-red-100 last:border-b-0">
        <span className="text-xs text-gray-500 shrink-0">{label}</span>
        <span className="text-sm font-medium text-gray-800 text-end break-all" dir={ltr ? 'ltr' : undefined}>
          {value}
        </span>
      </div>
    );

    return (
      <Dialog open onOpenChange={(o) => { if (!o) setConfirmState(null); }}>
        <DialogContent className="max-w-lg" dir={isRTL ? 'rtl' : 'ltr'}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <ShieldAlert className="w-5 h-5" />
              {resuming ? txt.resumeTitle : txt.confirmTitle}
            </DialogTitle>
          </DialogHeader>

          {!resolution ? (
            <div className="flex items-center gap-2 py-8 justify-center text-gray-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> {txt.resolving}
            </div>
          ) : blockedMessage ? (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{blockedMessage}</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">{resuming ? txt.resumeNote : txt.aboutToDelete}</p>

              <div className="rounded-lg border border-red-200 bg-red-50/60 px-3 py-2">
                {account ? (
                  <>
                    {row(txt.accountName, account.name || txt.noName)}
                    {row(txt.accountEmail, account.email || txt.noEmail, true)}
                    {row(txt.accountPhone, account.phone || txt.noEmail, true)}
                    {row(txt.accountJoined, formatDate(account.createdAt))}
                    {row(txt.accountUid, account.uid, true)}
                  </>
                ) : (
                  row(txt.accountUid, uidToConfirm, true)
                )}
              </div>

              {/* Phone ownership: stated, not implied. */}
              {ownership.proven ? (
                <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-2.5">
                  <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-green-800">{txt.ownershipProven} {ownership.method}</p>
                </div>
              ) : (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5">
                  <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-800">
                    <p className="font-semibold">{txt.ownershipUnprovenTitle}</p>
                    <p>{txt.ownershipUnproven}</p>
                    {ownership.claimed && <p className="mt-1">{txt.ownershipClaimed}</p>}
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500">{txt.typedNotice}</p>

              <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={checked}
                  onChange={(e) => setConfirmState((prev) => (
                    prev ? { ...prev, checked: e.target.checked } : prev
                  ))}
                />
                <span>{txt.confirmCheckbox}</span>
              </label>

              <p className="text-xs text-gray-400">{txt.retainedNote}</p>
            </div>
          )}

          <div className={`flex gap-2 pt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Button size="sm" variant="outline" onClick={() => setConfirmState(null)}>
              {txt.cancel}
            </Button>
            {uidToConfirm && (
              <Button
                size="sm"
                variant="destructive"
                disabled={!checked}
                onClick={() => runApproval(req, uidToConfirm)}
              >
                <Trash2 className={`w-3.5 h-3.5 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                {txt.confirmButton}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" dir={isRTL ? 'rtl' : 'ltr'}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              {txt.title}
              {pendingCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-xs font-bold">
                  {pendingCount}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Filter Tabs */}
          <div className="flex gap-1 border-b pb-2">
            {(['all', 'pending', 'approved', 'rejected'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                  filter === f
                    ? 'bg-[#004250] text-white'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {f === 'all' ? txt.all : f === 'pending' ? txt.pending : f === 'approved' ? txt.approved : txt.rejected}
                {f === 'pending' && pendingCount > 0 && (
                  <span className={`${isRTL ? 'mr-1' : 'ml-1'} text-xs`}>({pendingCount})</span>
                )}
              </button>
            ))}
          </div>

          {/* Failure banner — the account is still intact whenever this shows */}
          {actionError && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
              <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-red-700">{actionError.title}</p>
                <p className="text-red-600">{actionError.message}</p>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto space-y-3 pr-1">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <Trash2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="font-medium text-gray-500">{txt.empty}</p>
                <p className="text-sm text-gray-400 mt-1">{txt.emptyDesc}</p>
              </div>
            ) : (
              filtered.map((req) => (
                <div key={req.id} className="border rounded-lg p-4 space-y-3 bg-white">
                  {/* Top row: status + date */}
                  <div className="flex items-center justify-between">
                    {statusBadge(req.status)}
                    <span className="text-xs text-gray-400">
                      {/* -u-nu-latn-ca-gregory keeps Western digits and the Gregorian calendar in Arabic */}
                      {txt.submitted}: {req.createdAt.toLocaleDateString(dateLocale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>

                  {/* Details — all typed by the requester into a public form. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="font-medium text-gray-700" dir="ltr">{req.phone}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="text-gray-600" dir="ltr">{req.email || txt.noEmail}</span>
                    </div>
                  </div>

                  {req.reason && (
                    <div className="flex items-start gap-2 text-sm bg-gray-50 rounded-md p-2.5">
                      <MessageSquare className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                      <span className="text-gray-600">{req.reason}</span>
                    </div>
                  )}

                  {req.processedBy && (
                    <p className="text-xs text-gray-400">
                      {txt.processedBy}: {req.processedBy}
                    </p>
                  )}

                  {/* Actions */}
                  {req.status === 'pending' && (
                    <div className={`flex gap-2 pt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={processingId === req.id}
                        onClick={() => openConfirm(req)}
                        className="text-xs"
                      >
                        {processingId === req.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <>
                            <User className={`w-3.5 h-3.5 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                            {txt.approve}
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={processingId === req.id}
                        onClick={() => handleReject(req)}
                        className="text-xs"
                      >
                        <XCircle className={`w-3.5 h-3.5 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                        {txt.reject}
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {renderConfirm()}
    </>
  );
}
